import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {DateObservable} from "../../../stores/observables/dateObservable";
import {WeekObservableList} from "../../../stores/observables/weekObservableList";
import "./eventWeekEdit.sass";
import classNames from "classnames";
import {endOfMonth, format, startOfMonth} from "date-fns";
import {DayWeekState} from "../../../shared/enums/eventDayWeekState";
import parse from "html-react-parser";
import {utcToZonedTime} from "date-fns-tz";
import {Col, Row} from "reactstrap";
import {runInAction} from "mobx";
import {EventHelper} from "../../../shared/eventHelper";

export interface IEventWeekEditProps {
  eventStartDate: DateObservable;
  monthWeeks: WeekObservableList;
  timezone: string;
  isEditing: boolean;
}

const EventWeekEdit: React.FC<IEventWeekEditProps> = ({
  eventStartDate,
  monthWeeks,
  timezone,
  isEditing
}) => {
  const fourthWeek = 4;
  const lastWeek = -1;
  const {t} = useTranslation(["event"]);

  useEffect(() => {
    if (eventStartDate.date && !isEditing) {
      monthWeeks.clearSelections();
      let occurrence = EventHelper.getWeekOfMonth(
        eventStartDate.date,
        timezone
      );

      occurrence = occurrence > fourthWeek ? lastWeek : occurrence;
      const monthWeek = monthWeeks.monthWeekList.find(
        (value) => value.index === occurrence
      );

      runInAction(() => {
        monthWeek &&
          (monthWeek.state =
            getNumberOfDayInMonthOfSelectedDate(
              eventStartDate.date!,
              timezone
            ) === 4 &&
            (occurrence === fourthWeek || occurrence === lastWeek)
              ? DayWeekState.selected
              : DayWeekState.autoSelected);
      });
    }
  }, [eventStartDate.date]);

  function getNumberOfDayInMonthOfSelectedDate(
    selectedDate: Date,
    timezone: string
  ) {
    const localDate = utcToZonedTime(selectedDate, timezone);
    const firstDayOfMonth = startOfMonth(localDate);
    const lastDayOfMonth = endOfMonth(localDate);
    const dayOfWeek = localDate.getDay();
    const dayOfWeekIndex = dayOfWeek % 7; // Normalize to 0 (Sunday) to 6 (Saturday)

    let count = 0;
    let currentDay = firstDayOfMonth;

    while (currentDay <= lastDayOfMonth) {
      if (currentDay.getDay() === dayOfWeekIndex) {
        count++;
      }
      currentDay.setDate(currentDay.getDate() + 1);
    }
    return count;
  }

  function onWeekSelection(week: number) {
    const selectedMonthWeek = monthWeeks.monthWeekList.find(
      (value) => value.index === week
    );
    let occurrence = EventHelper.getWeekOfMonth(eventStartDate.date!, timezone);

    // Cannot un-check auto selected and disabled week number
    if (
      selectedMonthWeek?.state === DayWeekState.autoSelected ||
      selectedMonthWeek?.state === DayWeekState.disabled ||
      !selectedMonthWeek
    ) {
      return;
    }

    runInAction(() => {
      // Auto select 4th week to last and vice-versa only if the selected day is both the 4th and last week of month.
      if (
        occurrence === fourthWeek &&
        getNumberOfDayInMonthOfSelectedDate(eventStartDate.date!, timezone) ===
          4
      ) {
        // Auto select last week if 4th week is unselected.
        if (
          week === fourthWeek &&
          selectedMonthWeek.state === DayWeekState.selected
        ) {
          const lastWeekObservable = monthWeeks.monthWeekList.find(
            (value) => value.index === lastWeek
          );

          lastWeekObservable &&
            (lastWeekObservable.state = DayWeekState.selected);
        }

        // Auto select 4th week if last week is unselected.
        if (
          week === lastWeek &&
          selectedMonthWeek.state === DayWeekState.selected
        ) {
          const fourthWeekObservable = monthWeeks.monthWeekList.find(
            (value) => value.index === fourthWeek
          );

          fourthWeekObservable &&
            (fourthWeekObservable.state = DayWeekState.selected);
        }
      }

      // Toggle week number to selected and default state.
      selectedMonthWeek.state =
        selectedMonthWeek.state === DayWeekState.selected
          ? DayWeekState.default
          : DayWeekState.selected;

      // Disable remaining onOccurrenceSelection if 3 week numbers have been selected
      monthWeeks.disableEnableRemainingWeeks();
    });
  }

  return (
    <div className="event-week-edit" data-testid="event-week-edit">
      <Row xs={2}>
        <Col xs={12} className={"message-label"}>
          {eventStartDate.date &&
            parse(
              t("event:messages.monthWeek", {
                day: format(
                  utcToZonedTime(eventStartDate.date!, timezone),
                  "iiii"
                )
              })
            )}
        </Col>
        <Col xl={12} className={"week-container week-button-container"}>
          {monthWeeks.monthWeekList.map((value) => {
            let testId = `week_${value.index}`;
            return (
              <button
                data-testid={testId}
                key={value.index}
                onClick={(event) => onWeekSelection(value.index)}
                className={classNames("week-button", value.state)}
              >
                {t(value.label)}
              </button>
            );
          })}
        </Col>
      </Row>
    </div>
  );
};
export default observer(EventWeekEdit);
